// ABOUTME: Shared editor for the e-mail button colors (background + text) with a live example button.
// ABOUTME: Used by the warehouse and relation email settings views; persistence is delegated via callbacks.

import { Box, ButtonBase, InputBase, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../theme";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import { ShColorPicker } from "../../ShColorPicker";

// Matches the primary color hard-coded in the default email shell (wrapShellHtml).
export const DEFAULT_EMAIL_BUTTON_COLOR = "#31415f";
export const DEFAULT_EMAIL_BUTTON_TEXT_COLOR = "#ffffff";

const FIELDS = [
    {
        id: "button",
        labelKey: "email_button_color",
        inputTestId: "button-color-input",
        swatchTestId: "button-color-swatch",
    },
    {
        id: "text",
        labelKey: "email_button_text_color",
        inputTestId: "button-text-color-input",
        swatchTestId: "button-text-color-swatch",
    },
];

const EmailButtonColorSettings = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const committed = {
        button: props.buttonColor ?? DEFAULT_EMAIL_BUTTON_COLOR,
        text: props.buttonTextColor ?? DEFAULT_EMAIL_BUTTON_TEXT_COLOR,
    };
    const [draft, setDraft] = useState(committed);
    const [pickerField, setPickerField] = useState(null);
    const [pickerAnchor, setPickerAnchor] = useState(null);

    // Re-sync the drafts when a save round-trips through the settings query.
    useEffect(() => {
        setDraft({ button: committed.button, text: committed.text });
    }, [committed.button, committed.text]);

    const commit = (field) => {
        const value = draft[field];
        if (!value || value === committed[field]) return;
        if (field === "button") props.onButtonColorChange(value);
        else props.onButtonTextColorChange(value);
    };

    const openPicker = (field, anchor) => {
        if (props.disabled) return;
        setPickerField(field);
        setPickerAnchor(anchor);
    };

    const closePicker = () => {
        if (pickerField) commit(pickerField);
        setPickerField(null);
        setPickerAnchor(null);
    };

    return (
        <Box
            sx={{
                background:
                    theme.palette.mode === "dark"
                        ? colors.grey[400]
                        : colors.grey[0],
                border:
                    theme.palette.mode === "dark"
                        ? null
                        : `1px solid ${colors.grey[100]}`,
                boxShadow:
                    theme.palette.mode === "dark"
                        ? null
                        : `0px 2px 4px ${colors.grey[200]}`,
                borderRadius: 3,
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
            }}
        >
            <Subheading>{t("email_button_colors")}</Subheading>
            <Text light>{t("email_button_colors_subtitle")}</Text>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                }}
            >
                {FIELDS.map((field) => (
                    <Box
                        key={field.id}
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                        }}
                    >
                        <Text light>{t(field.labelKey)}</Text>
                        <Box
                            sx={{
                                background:
                                    theme.palette.mode === "dark"
                                        ? colors.grey[300]
                                        : colors.grey[100],
                                borderRadius: 4,
                                height: 32,
                                display: "flex",
                                alignItems: "center",
                                pl: 0.5,
                                pr: 1,
                                width: 140,
                            }}
                        >
                            <ButtonBase
                                data-testid={field.swatchTestId}
                                disabled={props.disabled}
                                onClick={(e) => openPicker(field.id, e.currentTarget)}
                                sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: 3,
                                    background: draft[field.id],
                                    flexShrink: 0,
                                    border: `1px solid ${colors.grey[200]}`,
                                }}
                            />
                            <InputBase
                                inputProps={{ "data-testid": field.inputTestId }}
                                disabled={props.disabled}
                                value={draft[field.id]}
                                onChange={(e) =>
                                    setDraft({ ...draft, [field.id]: e.target.value })
                                }
                                onBlur={() => commit(field.id)}
                                sx={{
                                    pl: 1,
                                    flex: 1,
                                    color: colors.txt["primary"],
                                    fontSize: 14,
                                }}
                            />
                        </Box>
                    </Box>
                ))}
            </Box>
            <Text light>{t("email_button_example")}</Text>
            <Box
                sx={{
                    background:
                        theme.palette.mode === "dark"
                            ? colors.grey[300]
                            : colors.bg["tertiary"],
                    borderRadius: 3,
                    p: 3,
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                <Box
                    data-testid="email-button-preview"
                    style={{
                        backgroundColor: draft.button,
                        color: draft.text,
                    }}
                    sx={{
                        px: 3,
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: 14,
                    }}
                >
                    {t("email_button_example_text")}
                </Box>
            </Box>

            <ShColorPicker
                value={{ hex: pickerField ? draft[pickerField] : committed.button, opacity: 1 }}
                setValue={(value) =>
                    pickerField && setDraft((prev) => ({ ...prev, [pickerField]: value.hex }))
                }
                open={Boolean(pickerAnchor)}
                anchorEl={pickerAnchor}
                onClose={closePicker}
            />
        </Box>
    );
};

export default EmailButtonColorSettings;
